import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { API_BASE } from "../lib/api";

// Public route. Always shows the same success message so this endpoint
// can't be used to probe for accounts.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/password/request`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="forgot-page">
      <div className="w-full max-w-sm space-y-6">
        <div>
          <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="back-to-login">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
          <h1 className="font-display text-3xl font-bold">Forgot password?</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter your email and we'll send you a reset link. It expires in 30 minutes.</p>
        </div>
        {!sent ? (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     autoComplete="email" data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send reset link
            </Button>
          </form>
        ) : (
          <div className="p-6 rounded-2xl border border-border bg-secondary/40 space-y-3" data-testid="forgot-sent">
            <Mail className="w-8 h-8 text-[hsl(var(--gain))]" />
            <div>
              <div className="font-medium">Check your email.</div>
              <div className="text-sm text-muted-foreground mt-1">
                If <span className="font-mono-tab">{email}</span> is a Aanar account, you'll receive a reset link within a minute. Didn't get it? Check spam, then try again.
              </div>
            </div>
            <Button asChild variant="outline" className="rounded-full w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
